import { Box, Divider } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { assetKeys, scenarioSetKeys } from "../../../../../api/queryKeys";
import ConfirmationDialog from "../../../../../components/dialogs/ConfirmationDialog";
import { Toast } from "../../../../../components/Toast";
import {
  ASSET_CARD_SIZE,
  ManualSteps,
  PageDisplayMode,
} from "../../../../../utils/constants";
import CreateNewAssetModal from "../wizard/CreateNewAssetModal";
import AssetActionMenu from "./AssetActionMenu";
import AssetCardActionButtons from "./AssetCardActionButtons";
import ContentAssetCard from "./ContentAssetCard";

export default function RegularAssetCard({
  asset,
  entitiesInAsset,
  onClick,
  selected,
  hasPermission,
  displayMode,
}) {
  const colors = useColors();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const { trackEvent, deleteAsset } = useAPI();
  const queryClient = useQueryClient();

  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
  };

  const handleOpenUpdateModal = (e) => {
    e?.stopPropagation();
    trackEvent(`User is opening update modal for asset ${asset.name}`);
    setOpenUpdateModal(true);
  };

  const handleOpenDeleteModal = (e) => {
    e?.stopPropagation();
    trackEvent(`User is opening delete modal for asset ${asset.name}`);
    setOpenDeleteModal(true);
  };

  const handleDelete = () => {
    trackEvent(`User is deleting asset ${asset.name}`).then();
    setDeleteIsLoading(true);
    deleteAsset(asset.asset_id)
      .then(() => {
        setDeleteIsLoading(false);
        Toast.success("Successfully deleted");
        queryClient.invalidateQueries({
          queryKey: assetKeys.all,
        });
        queryClient.invalidateQueries({
          queryKey: scenarioSetKeys.all,
        });
        setOpenDeleteModal(false);
      })

      .catch(() => {
        setDeleteIsLoading(false);

        Toast.error("Error deleting");
      });
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  // Avoid setting url "asset" param again (in AssetView) on each click in SectionCard children
  const onCardClick = () => {
    if (!selected && !openUpdateModal && !openDeleteModal) {
      onClick();
    }
  };
  const DisplayCardStyle = {
    width: ASSET_CARD_SIZE.WIDTH_CARD,
    height: ASSET_CARD_SIZE.HEIGHT_CARD,
    gap: "8px",
    flexDirection: "row",
  };
  const DisplayTableStyle = {
    width: ASSET_CARD_SIZE.WIDTH,
    height: ASSET_CARD_SIZE.HEIGHT,
    gap: "12px",
    flexDirection: "column",
  };
  return (
    <Box
      position="relative"
      sx={{
        mt: 1,
        display: "flex",
        ...(displayMode === PageDisplayMode.CARD
          ? DisplayCardStyle
          : DisplayTableStyle),
        padding: 3,
        borderRadius: "16px",
        cursor: "pointer",
        justifyContent: "space-between",
        alignContent: "space-between",
        boxShadow: selected
          ? "0px 0px 6px 1px " + colors.primary_100 + " inset"
          : "0px 0px 5.3px 0px " + colors.primary_32percent,
        "&:hover": {
          boxShadow: "0px 0px 4px 1px " + colors.primary_50,
        },
      }}
      onClick={onCardClick}
    >
      <ContentAssetCard
        asset={asset}
        assetId={asset?.asset_id}
        assetType={asset?.type}
        assetName={asset?.name}
        numEntityIds={asset?.entity_ids?.length}
        scenarioSetsCount={asset?.scenario_sets_count}
        displayMode={displayMode}
      >
        {displayMode === PageDisplayMode.CARD && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              gap: "4px",
              alignItems: "flex-end",
            }}
          >
            <AssetActionMenu
              handleOpenUpdateModal={handleOpenUpdateModal}
              handleDeleteAsset={handleOpenDeleteModal}
              hasPermission={hasPermission}
            />
          </Box>
        )}
      </ContentAssetCard>
      {displayMode !== PageDisplayMode.CARD && (
        <>
          <Divider />
          <AssetCardActionButtons
            onEdit={handleOpenUpdateModal}
            onDelete={handleOpenDeleteModal}
            hasPermission={hasPermission}
          />
        </>
      )}

      <CreateNewAssetModal
        open={openUpdateModal}
        onClose={handleCloseUpdateModal}
        assetToEdit={{ asset: asset, entities: entitiesInAsset }}
        steps={ManualSteps}
      />
      <ConfirmationDialog
        openDialog={openDeleteModal}
        handleDialogClose={handleCloseDeleteModal}
        handleConfirm={handleDelete}
        dialogTitle="Delete Asset and Remove Attack Scenarios"
        dialogContentFirstLine="Deleting this critical asset will remove all attack scenarios identified by Breeze for this asset."
        dialogContentSecondLine="Are you sure you want to proceed?"
        confirmationButtonText="Delete"
        cancelButtonText="Cancel"
        isLoading={deleteIsLoading}
        destructive
      />
    </Box>
  );
}
